import React, { useEffect, useRef, useState } from 'react';
import { useLang } from '../../../_LangContext';
import { mergeClasses } from '@griffel/react';
import WaveSurfer from 'wavesurfer.js';

const UISoundBtn = ({ lang, className, filepath, filename, onStateChange }) =>
{
    const { getText } = useLang();
    const [audioState, setAudioState] = useState('stopped');
    const [isLoaded, setIsLoaded] = useState(false);
    
    const waveSurfer = useRef(null);
    const waveformRef = useRef(null);

    // Support both filepath (direct) and filename (constructed) props for backward compatibility
    const audioPath = filepath || `${process.env.PUBLIC_URL}/audio/${filename}.mp3`;

    className = mergeClasses('sound-btn', className, !isLoaded ? 'loading' : '');

    useEffect(() =>
    {
        // Initialize WaveSurfer
        waveSurfer.current = WaveSurfer.create(
        {
            container: waveformRef.current,
            waveColor: '#bad9f7',
            progressColor: '#0070E0',
            cursorColor: 'transparent',
            barWidth: 2,
            barRadius: 2,
            responsive: false,
            height: 18,
            normalize: true,
            partialRender: true,
            mediaType: "audio/mpeg",
            backend: 'WebAudio',
        });

        // Only set loading state on ready
        waveSurfer.current.on('ready', () => 
        {
            setIsLoaded(true);
        });

        // Handle finish event to reset state
        waveSurfer.current.on('finish', () => 
        {
            setAudioState('stopped');
            if (onStateChange) onStateChange('stop', audioPath);
        });

        // Load the audio file
        waveSurfer.current.load(audioPath);

        // Cleanup
        return () => 
        {
            if (waveSurfer.current) 
            {
                waveSurfer.current.destroy();
                setIsLoaded(false);
            }
        };
    },
    [audioPath]);

    const handlePlay = () =>
    {
        if (!isLoaded) return;

        if (!waveSurfer.current.isPlaying())
        {
            waveSurfer.current.play();
            setAudioState('playing');
            if (onStateChange) onStateChange('play', audioPath);
        }
    };

    const handleStop = () =>
    {
        if (!isLoaded) return;
        
        waveSurfer.current.stop();
        setAudioState('stopped');
        if (onStateChange) onStateChange('stop', audioPath);
    };

    const handlePlayPause = () =>
    {
        if (!isLoaded) return;
        
        if (!waveSurfer.current.isPlaying())
        {
            waveSurfer.current.play();
            setAudioState('playing');
            if (onStateChange) onStateChange('resume', audioPath);
        }
        else
        {
            waveSurfer.current.pause();
            setAudioState('paused');
            if (onStateChange) onStateChange('pause', audioPath);
        }
    };

    return (
        <button 
            className={`${className} ${audioState}`} 
            disabled={!isLoaded}
            data-filepath={audioPath}
        >
            <div className={`btn-label`} onClick={handlePlay}>
                <p className="icon play" />
                <div className="text">
                    {getText('play-sound')}
                </div>
            </div>
            <div className="player">
                <div className="icon-circle pause" onClick={handlePlayPause}>
                    <p className="icon pause" />
                </div>
                <div className="waveform" ref={waveformRef} />
                <div className="icon-circle stop" onClick={handleStop}>
                    <p className="icon stop" />
                </div>
            </div>
        </button>
    );
};

export default UISoundBtn;